import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Button,
  CircularProgress,
} from '@mui/material';
import { TrendingUp, Search as SearchIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import axios from '../utils/axios';
import { useSelector } from 'react-redux';

const COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#0097a7',
  '#d32f2f',
  '#5d4037',
  '#00796b',
  '#afb42b',
  '#c2185b',
];

export default function MyIncome() {
  const theme = useTheme();
  const { user } = useSelector((state) => state.auth);
  const canViewEmployeeIncome = user?.permissions?.viewEmployeeIncome || user?.role === 'admin';

  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employees, setEmployees] = useState([]);

  const [activities, setActivities] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);

  const [chartRows, setChartRows] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [totals, setTotals] = useState({ kg: 0, money: 0, pieces: 0 });

  const [metric, setMetric] = useState('money');
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (canViewEmployeeIncome) {
      const fetchEmployees = async () => {
        try {
          const { data } = await axios.get('/users');
          const workers = data.filter((u) => u.role === 'worker' && u.isActive && u.ms_id);
          setEmployees(workers);
          if (workers.length > 0) {
            setSelectedEmployee(workers[0].ms_id);
          }
        } catch (error) {
          console.error('Ошибка загрузки сотрудников:', error);
        }
      };
      fetchEmployees();
    }
  }, [canViewEmployeeIncome]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        start: startDate.format('YYYY-MM-DD'),
        end: endDate.format('YYYY-MM-DD'),
      };

      if (canViewEmployeeIncome && selectedEmployee) {
        params.employeeId = selectedEmployee;
      }

      const { data } = await axios.get('/worker/dashboard', { params });
      const baseActivities = data.activities || [];
      const allActs = [...baseActivities, { id: 'total', name: 'Всего' }];
      setActivities(allActs);
      setChartRows(data.chartData || []);
      setTableRows(data.tableData || []);
      setTotals(data.totals || { kg: 0, money: 0, pieces: 0 });
      setSelectedActivities((prev) => {
        if (prev.length === 0 || prev.length !== allActs.length) {
          return allActs.map((a) => a.id);
        }
        return prev;
      });
      setDataLoaded(true);
    } catch (e) {
      console.error('Ошибка загрузки данных дашборда', e);
    } finally {
      setLoading(false);
    }
  };

  const pivotChartData = useMemo(() => {
    const dateArr = [];
    let d = startDate.clone();
    while (d.isBefore(endDate) || d.isSame(endDate, 'day')) {
      dateArr.push(d.format('DD.MM.YYYY'));
      d = d.add(1, 'day');
    }

    const map = {};
    dateArr.forEach((dt) => {
      map[dt] = { date: dt };
      activities.forEach((act) => {
        map[dt][act.name] = 0;
      });
    });

    chartRows.forEach((row) => {
      const dateKey = dayjs(row.date).format('DD.MM.YYYY');
      const value = metric === 'money' ? Number(row.money) : Number(row.pieces);
      const actName = row.activity_name;
      if (map[dateKey]) {
        map[dateKey][actName] = (map[dateKey][actName] || 0) + value;
      }
    });

    Object.values(map).forEach((obj) => {
      const totalVal = activities
        .filter((a) => a.id !== 'total')
        .reduce((sum, a) => sum + (obj[a.name] || 0), 0);
      obj['Всего'] = totalVal;
    });

    return Object.values(map).sort((a, b) => dayjs(a.date, 'DD.MM.YYYY') - dayjs(b.date, 'DD.MM.YYYY'));
  }, [chartRows, metric, startDate, endDate, activities]);

  const handleMetricChange = (event, newMetric) => {
    if (newMetric) setMetric(newMetric);
  };

  const getPageTitle = () => {
    if (canViewEmployeeIncome) {
      const selectedEmp = employees.find((emp) => emp.ms_id === selectedEmployee);
      return selectedEmp ? `Доход сотрудника: ${selectedEmp.firstName} ${selectedEmp.lastName}` : 'Доход сотрудников';
    }
    return 'Мой доход';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {getPageTitle()}
        </Typography>

        {/* Фильтры */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {canViewEmployeeIncome && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Сотрудник</InputLabel>
                  <Select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    label="Сотрудник"
                  >
                    {employees.map((emp) => (
                      <MenuItem key={emp.ms_id} value={emp.ms_id}>
                        {emp.firstName} {emp.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={6} md={2}>
              <DatePicker
                label="Начало"
                value={startDate}
                onChange={(newVal) => setStartDate(newVal)}
                format="DD.MM.YYYY"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <DatePicker
                label="Конец"
                value={endDate}
                onChange={(newVal) => setEndDate(newVal)}
                format="DD.MM.YYYY"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Тип производства</InputLabel>
                <Select
                  multiple
                  value={selectedActivities}
                  onChange={(e) => setSelectedActivities(e.target.value)}
                  renderValue={(selected) =>
                    selected.map((id) => activities.find((a) => a.id === id)?.name || id).join(', ')
                  }
                  disabled={activities.length === 0}
                  label="Тип производства"
                  notched
                >
                  {activities.length === 0 ? (
                    <MenuItem disabled>
                      <ListItemText primary="Нет данных" />
                    </MenuItem>
                  ) : (
                    activities.map((act) => (
                      <MenuItem key={act.id} value={act.id}>
                        <Checkbox checked={selectedActivities.includes(act.id)} />
                        <ListItemText primary={act.name} />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <ToggleButtonGroup value={metric} exclusive onChange={handleMetricChange} size="small" fullWidth>
                <ToggleButton value="money">₽</ToggleButton>
                <ToggleButton value="pieces">Шт</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <Button
                variant="contained"
                onClick={fetchData}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                fullWidth
                sx={{ height: 40 }}
              >
                {loading ? 'Загрузка...' : 'Загрузить'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* График */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                {metric === 'money' ? 'Доход по дням' : 'Штуки по дням'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                За выбранный период
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUp color="success" />
              <Typography color="success.main">
                {metric === 'money' ? `₽${totals.money.toFixed(0)}` : `${totals.pieces} шт`}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ height: 320 }}>
            {pivotChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={pivotChartData}>
                  <defs>
                    {activities.filter(a => selectedActivities.includes(a.id)).map((act, idx) => (
                      <linearGradient key={act.id} id={`color${act.id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS[activities.indexOf(act) % COLORS.length]} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS[activities.indexOf(act) % COLORS.length]} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    tickFormatter={(v) => metric === 'money' ? `${(v / 1000).toFixed(0)}K` : `${v}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 8,
                      boxShadow: theme.shadows[4],
                    }}
                    formatter={(value, name) => [
                      metric === 'money' ? `₽${Number(value).toLocaleString()}` : `${value} шт`,
                      name
                    ]}
                    labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                  />
                  <Legend />
                  {activities.filter(a => selectedActivities.includes(a.id)).map((act) => (
                    <Area
                      key={act.id}
                      type="monotone"
                      dataKey={act.name}
                      stroke={COLORS[activities.indexOf(act) % COLORS.length]}
                      strokeWidth={2}
                      fill={`url(#color${act.id})`}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                {dataLoaded ? 'Нет данных для отображения' : 'Нажмите "Загрузить" для получения данных'}
              </Box>
            )}
          </Box>
        </Paper>

        {/* Таблица */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
            Оплата по товарам
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Товар</TableCell>
                  <TableCell align="right">Шт</TableCell>
                  <TableCell align="right">Оплата</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Typography variant="body2">{row.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.activity_name}</Typography>
                    </TableCell>
                    <TableCell align="right">{row.pieces}</TableCell>
                    <TableCell align="right">{Number(row.money).toFixed(0)} ₽</TableCell>
                  </TableRow>
                ))}
                {tableRows.length > 0 && (
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Итого</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.pieces}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.money.toFixed(0)} ₽</TableCell>
                  </TableRow>
                )}
                {tableRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {dataLoaded ? 'Нет данных за выбранный период' : 'Нажмите "Загрузить" для получения данных'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}
